// src/utils/dateUtils.ts

import {
  format,
  isToday,
  isYesterday,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  formatRelative,
} from 'date-fns';
import { enUS, fr, zhCN, Locale as DateFNSLocale } from 'date-fns/locale';
import { TFunction } from 'next-i18next';
import { Locale } from './languages';

/**
 * Parse a date string from backend and convert to UTC.
 * Backend sends dates in UTC+2 timezone, but we need to display them as UTC.
 *
 * @param dateString - ISO date string from backend (in UTC+2 timezone)
 * @returns Date object representing the UTC equivalent
 */
export const parseUTCDate = (dateString: string): Date => {
  const trimmedDateString = dateString.trim();

  // Check if the string already has timezone info using proper regex
  // Matches: Z, +HH:MM, +HHMM, -HH:MM, -HHMM patterns
  const timezoneRegex = /Z$|[+-]\d{2}:?\d{2}$/;

  if (timezoneRegex.test(trimmedDateString)) {
    return new Date(trimmedDateString);
  }

  // For ISO strings without timezone info (e.g., "2023-12-25T10:30:00"),
  // Backend sends these in UTC+2 timezone, so we need to subtract 2 hours to get UTC
  if (trimmedDateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?$/)) {
    // Parse as UTC+2, JavaScript automatically converts to UTC
    return new Date(`${trimmedDateString}+02:00`);
  }

  // For other date formats, assume they're in backend's UTC+2 timezone
  // Parse as local first, then adjust for UTC+2 offset
  const localDate = new Date(trimmedDateString);

  // Check if the date is valid
  if (isNaN(localDate.getTime())) {
    return new Date(NaN);
  }

  // Backend uses UTC+2, so we need to subtract 2 hours to get UTC
  const utcTime = localDate.getTime() - 2 * 60 * 60 * 1000;

  return new Date(utcTime);
};

/** Map our app locales to date-fns locales */
const dateFnsLocale = {
  en: enUS,
  fr,
  zh: zhCN,
} as const;

const getDateFnsLocale = (locale: Locale): DateFNSLocale => dateFnsLocale[locale] || enUS;

/** “May 22, 2025” / “22 mai 2025” / “2025年5月22日” */
export const formatDateFull = (value: string | Date, locale: Locale = 'en'): string => {
  const d = new Date(value);
  if (locale === 'zh') {
    return format(d, 'yyyy年M月d日', {
      locale: getDateFnsLocale(locale),
    });
  }
  return format(d, 'MMMM d, yyyy', {
    locale: getDateFnsLocale(locale),
  });
};

/** “2025-05-22” (same everywhere) */
export const formatDateDashed = (value: string | Date): string => format(new Date(value), 'yyyy-MM-dd');

/** “+02:00” */
function formatOffset(d: Date): string {
  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

/**
 * Local timestamp with UTC offset:
 * - zh: "2025年05月22日 HH:mm (UTC+01:00)"
 * - others: "2025-05-22 | HH:mm (UTC+01:00)"
 */
export const utcDate = (value: string | Date, locale: Locale = 'en'): string => {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
    return '';
  }
  // Use local time instead of UTC
  const localYear = d.getFullYear();
  const localMonth = String(d.getMonth() + 1).padStart(2, '0');
  const localDay = String(d.getDate()).padStart(2, '0');
  const localHours = String(d.getHours()).padStart(2, '0');
  const localMinutes = String(d.getMinutes()).padStart(2, '0');
  const offset = formatOffset(d);

  if (locale === 'zh') {
    return `${localYear}年${localMonth}月${localDay}日 ${localHours}:${localMinutes} (UTC${offset})`;
  }
  return `${localYear}-${localMonth}-${localDay} | ${localHours}:${localMinutes} (UTC${offset})`;
};

/** “Published on…” / “Updated at…” */
export const formatDateWithLabel = (
  updatedAt: Date | undefined,
  createdAt: Date,
  locale: Locale,
  t: (key: string) => string,
): string => {
  const dateLabel = t(updatedAt ? 'label.updatedAt' : 'label.publishedOn');
  const formatted = utcDate(updatedAt || createdAt, locale);
  return `${dateLabel} ${formatted}`;
};

/**
 * Conversation timestamp:
 * Standard format for all messages: "yyyy-MM-dd | HH:mm (UTC+XX:XX)"
 * - zh: "yyyy年MM月dd日 | HH:mm (UTC+XX:XX)"
 * - others: "yyyy-MM-dd | HH:mm (UTC+XX:XX)"
 */
export function formatConversationDate(value: string | Date, locale: Locale = 'en'): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  const time = format(d, 'HH:mm', { locale: getDateFnsLocale(locale) });
  const offset = formatOffset(d);

  if (locale === 'zh') {
    const day = format(d, 'yyyy年MM月dd日', {
      locale: getDateFnsLocale(locale),
    });
    return `${day} | ${time} (UTC${offset})`;
  }

  const day = format(d, 'yyyy-MM-dd');
  return `${day} | ${time} (UTC${offset})`;
}

/**
 * Last reply timestamp:
 * Format: "today at HH:mm UTC+XX:XX" for today, or "yyyy-MM-dd at HH:mm UTC+XX:XX" for other dates
 */
export function formatLastReplyTime(value: string | Date, locale: Locale = 'en'): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  const time = format(d, 'HH:mm', { locale: getDateFnsLocale(locale) });
  const offset = formatOffset(d);

  if (isToday(d)) {
    return `today at ${time} UTC${offset}`;
  }

  const day = format(d, 'yyyy-MM-dd');
  return `${day} at ${time} UTC${offset}`;
}

/**
 * Relative conversation date (e.g. “just now”, “5 minutes ago”),
 * falling back to YYYY-MM-DD for old dates.
 */
export function formatConversationDateWithOffset(t: TFunction, d: Date): string {
  const now = new Date();

  if (isToday(d)) {
    const offset = formatOffset(d);
    return `${format(d, 'HH:mm')} ${offset}`;
  }

  const seconds = differenceInSeconds(now, d);
  if (seconds < 60) {
    return t('label.justNow');
  }

  const minutes = differenceInMinutes(now, d);
  if (minutes < 60) {
    return minutes === 1 ? t('label.minuteAgo') : t('label.minutesAgo', { count: minutes });
  }

  const hours = differenceInHours(now, d);
  if (hours < 24) {
    return hours === 1 ? t('label.hourAgo') : t('label.hoursAgo', { count: hours });
  }

  const days = differenceInDays(now, d);
  if (days < 30) {
    return days === 1 ? t('label.dayAgo') : t('label.daysAgo', { count: days });
  }

  const months = differenceInMonths(now, d);
  if (months < 12) {
    return months === 1 ? t('label.monthAgo') : t('label.monthsAgo', { count: months });
  }

  const years = differenceInYears(now, d);
  return years === 1 ? t('label.yearAgo') : t('label.yearsAgo', { count: years });
}

/**
 * Divider label for days in chat:
 * - locale’s own “today”/“yesterday” (via formatRelative)
 * - otherwise formatted date:
 *     • zh: “yyyy年MM月dd日”
 *     • others: “dd.MM.yyyy”
 */
export function formatDividerDate(value: string | Date, locale: Locale): string {
  const d = typeof value === 'string' ? new Date(value) : value;

  if (isToday(d) || isYesterday(d)) {
    const raw = formatRelative(d, new Date(), {
      locale: getDateFnsLocale(locale),
    });
    return raw.split(' ')[0];
  }

  return format(d, locale === 'zh' ? 'yyyy年MM月dd日' : 'dd.MM.yyyy', { locale: getDateFnsLocale(locale) });
}
